import { useLayoutEffect, useSyncExternalStore } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateMyLocale, type Me } from "../api";
import { sessionKey, useSession } from "../session";
import { isLocale, messages, type Locale, type Messages } from "./messages";

export { LOCALES, LOCALE_NAMES, type Locale, type Messages } from "./messages";

/**
 * The interface language (ADR-0023).
 *
 * A signed-in account's language is the one stored on it, so it follows the
 * account to another device and an admin can set it. A visitor's lives on the
 * device, starting from the browser's language, and is the one kept after
 * signing out — whatever they last chose.
 */

const KEY = "sageleaf.locale";

function initial(): Locale {
  try {
    const stored = localStorage.getItem(KEY);
    if (isLocale(stored)) return stored;
  } catch {
    // Storage disabled: fall through to the browser's language.
  }
  return navigator.language.toLowerCase().startsWith("fa") ? "fa" : "en";
}

let deviceLocale: Locale = initial();
const listeners = new Set<() => void>();

function setDeviceLocale(locale: Locale) {
  deviceLocale = locale;
  try {
    localStorage.setItem(KEY, locale);
  } catch {
    // The choice lasts this visit.
  }
  listeners.forEach((notify) => notify());
}

function subscribe(notify: () => void) {
  listeners.add(notify);
  return () => listeners.delete(notify);
}

/** useLocale is the language the interface is in right now. */
export function useLocale(): Locale {
  const { me } = useSession();
  const device = useSyncExternalStore(subscribe, () => deviceLocale);
  return isLocale(me?.user.locale) ? me.user.locale : device;
}

/** useT is every string, in the current language. */
export function useT(): Messages {
  return messages[useLocale()];
}

/**
 * useSetLocale switches the language at once and saves it where it belongs.
 *
 * The session is updated before the request returns, so the page turns on the
 * tap rather than a round trip later; a failed save refetches the session and
 * puts back what the server holds.
 */
export function useSetLocale() {
  const client = useQueryClient();
  const { me } = useSession();
  const save = useMutation({
    mutationFn: updateMyLocale,
    onSuccess: (fresh) => client.setQueryData(sessionKey, fresh),
    onError: () => client.invalidateQueries({ queryKey: sessionKey }),
  });
  return (locale: Locale) => {
    setDeviceLocale(locale);
    if (!me) return;
    client.setQueryData<Me>(sessionKey, { ...me, user: { ...me.user, locale } });
    save.mutate(locale);
  };
}

/**
 * useDocumentLocale puts the language and its direction on the document root.
 *
 * `dir="rtl"` on <html> mirrors the chrome: flex rows, logical margins, the
 * drawer's side. Authored text keeps its own `dir="auto"` blocks, which are not
 * nested inside anything carrying `dir="auto"`, so the rule in CLAUDE.md holds.
 */
export function useDocumentLocale() {
  const locale = useLocale();
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.lang = locale;
    root.dir = locale === "fa" ? "rtl" : "ltr";
    // The tab carries the name in the interface's language too.
    document.title = messages[locale].nav.brand;
  }, [locale]);
}
